import { login, getUserInfo } from '@/api/auth'
import { resetDynamicRoutes } from '@/router'

const storedUserInfo = JSON.parse(localStorage.getItem('userInfo') || 'null')

const state = {
  token: localStorage.getItem('token') || '',
  userInfo: storedUserInfo,
  // 只表示本次 JS 会话已经从服务端取得完整资料；刷新页面后必须重新校验。
  profileLoaded: false,
  permissions: [],
  roleCodes: Array.from((storedUserInfo && storedUserInfo.roleCodes) || [])
}

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token
    localStorage.setItem('token', token)
  },
  SET_USER_INFO(state, info) {
    state.userInfo = info
    localStorage.setItem('userInfo', JSON.stringify(info))
  },
  SET_PROFILE_LOADED(state, loaded) {
    state.profileLoaded = Boolean(loaded)
  },
  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions
  },
  SET_ROLE_CODES(state, codes) {
    state.roleCodes = codes
  },
  CLEAR_ALL(state) {
    state.token = ''
    state.userInfo = null
    state.profileLoaded = false
    state.permissions = []
    state.roleCodes = []
    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
  }
}

const isOfflineLoginEnabled = () => {
  if (process.env.NODE_ENV !== 'development') return false
  // 离线账号会伪造平台超管身份，只允许开发者显式开启，默认必须 fail-closed。
  if (typeof process.env.VUE_APP_ENABLE_OFFLINE_LOGIN === 'undefined') return false
  return String(process.env.VUE_APP_ENABLE_OFFLINE_LOGIN).toLowerCase() === 'true'
}

const isBackendUnavailable = (error) => {
  const msg = String(error?.message || '')
  return (
    msg.includes('Could not proxy request') ||
    msg.includes('ECONNREFUSED') ||
    msg.includes('Network Error') ||
    error?.code === 'ECONNREFUSED' ||
    error?.response?.status >= 500
  )
}

const buildOfflineUserInfo = (loginForm = {}) => ({
  username: loginForm.username || process.env.VUE_APP_OFFLINE_USERNAME || 'admin',
  displayName: loginForm.username || process.env.VUE_APP_OFFLINE_USERNAME || '开发管理员',
  permissions: ['*'],
  roleCodes: [],
  menus: [],
  isSuperAdmin: true
})

const actions = {
  async login({ commit, dispatch }, loginForm) {
    try {
      const res = await login(loginForm)
      const { token, userInfo } = res.data
      commit('SET_TOKEN', token)
      commit('SET_USER_INFO', userInfo)
      commit('SET_PERMISSIONS', Array.from(userInfo.permissions || []))
      commit('SET_ROLE_CODES', Array.from(userInfo.roleCodes || []))
      // 生成动态路由
      await dispatch('permission/generateRoutes', userInfo.menus, { root: true })
      commit('SET_PROFILE_LOADED', true)
      return res
    } catch (error) {
      if (isOfflineLoginEnabled() && isBackendUnavailable(error)) {
        const offlineUserInfo = buildOfflineUserInfo(loginForm)
        const fakeToken = `dev-token-${Date.now()}`
        commit('SET_TOKEN', fakeToken)
        commit('SET_USER_INFO', offlineUserInfo)
        commit('SET_PERMISSIONS', Array.from(offlineUserInfo.permissions || []))
        commit('SET_ROLE_CODES', Array.from(offlineUserInfo.roleCodes || []))
        await dispatch('permission/generateRoutes', offlineUserInfo.menus || [], { root: true })
        commit('SET_PROFILE_LOADED', true)
        return { data: { token: fakeToken, userInfo: offlineUserInfo } }
      }
      throw error
    }
  },

  async refreshUserInfo({ commit, dispatch }) {
    const res = await getUserInfo()
    const info = res.data
    commit('SET_USER_INFO', info)
    commit('SET_PERMISSIONS', Array.from(info.permissions || []))
    commit('SET_ROLE_CODES', Array.from(info.roleCodes || []))
    await dispatch('permission/generateRoutes', info.menus, { root: true })
    commit('SET_PROFILE_LOADED', true)
  },

  async logout({ commit, state }) {
    const token = state.token
    commit('CLEAR_ALL')
    resetDynamicRoutes()
    if (token) {
      // 使用原生请求，避免401拦截器再次触发logout；同步注销控制台复用的身份。
      try {
        await fetch('/api/auth/logout', {
          method: 'POST', headers: { Authorization: `Bearer ${token}` },
          signal: AbortSignal.timeout(5000)
        })
      } catch (_) { /* 网络不可用时仍完成本地退出 */ }
    }
  }
}

const getters = {
  hasPermission: (state) => (code) => {
    if (state.permissions.includes('*')) return true
    return state.permissions.includes(code)
  },
  isSuperAdmin: (state) => {
    return state.userInfo && state.userInfo.isSuperAdmin
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
